import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { DarkModeToggle } from '../components/DarkModeToggle';
import { usersAPI, settingsAPI, authAPI } from '../services/api';
import { toast } from 'sonner';
import { User, Mail, Lock, Phone, MapPin, Eye, EyeOff, Ticket, AlertTriangle, MessageCircle, X, ArrowLeft, KeyRound, Loader2 } from 'lucide-react';

export default function Login() {
  const [searchParams] = useSearchParams();
  const [isLogin, setIsLogin] = useState(true);
  const [showPassword, setShowPassword] = useState(false);
  const [centerInfo, setCenterInfo] = useState(null);
  const [siteSettings, setSiteSettings] = useState({ siteName: 'TCF Canada', logo: '' });
  const [blockedPopup, setBlockedPopup] = useState({ show: false, message: '', whatsapp: '' });

  // 'login' | 'forgot' | 'reset'
  const [authView, setAuthView] = useState('login');
  const [forgotEmail, setForgotEmail] = useState('');
  const [resetCode, setResetCode] = useState('');
  const [resetPassword, setResetPassword] = useState('');
  const [resetPasswordConfirm, setResetPasswordConfirm] = useState('');
  const [resetEmail, setResetEmail] = useState('');
  const [showResetPassword, setShowResetPassword] = useState(false);
  const [forgotLoading, setForgotLoading] = useState(false);

  // Login fields
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  
  // Registration fields
  const [registerForm, setRegisterForm] = useState({
    nom: '',
    email: '',
    telephone: '',
    pays: '',
    username: '',
    password: '',
    confirmPassword: '',
    invitationCode: ''
  });
  
  const { login, register, user } = useAuth();
  const navigate = useNavigate();

  // Check for invitation code in URL and load appropriate branding
  useEffect(() => {
    const code = searchParams.get('code');
    
    const loadSettings = async () => {
      if (code) {
        setRegisterForm(prev => ({ ...prev, invitationCode: code.toUpperCase() }));
        
        // Store the center code for logout redirect
        localStorage.setItem('tcf_center_code', code.toUpperCase());
        
        // Find the center associated with this code via API
        try {
          const result = await usersAPI.getAdminByCode(code.toUpperCase());
          if (result?.found && result?.admin) {
            setCenterInfo({
              name: result.admin.nom || result.admin.username,
              code: code.toUpperCase()
            });
            
            // Load center-specific settings ONLY (no SuperAdmin merge)
            const centerSettings = await settingsAPI.getCenterSettings(result.admin.id);
            
            // Use Admin's settings with neutral defaults (NOT SuperAdmin's branding)
            setSiteSettings({
              siteName: centerSettings?.site_name || centerSettings?.siteName || 'TCF Canada',
              logo: centerSettings?.logo || '',
              whatsapp: centerSettings?.whatsapp || '',
              phone: centerSettings?.phone || '',
              email: centerSettings?.email || ''
            });
          } else {
            // Invalid code, load default settings
            localStorage.removeItem('tcf_center_code');
            const settings = await settingsAPI.getSiteSettings();
            setSiteSettings({
              siteName: settings?.site_name || settings?.siteName || 'TCF Canada',
              logo: settings?.logo || '',
              whatsapp: settings?.whatsapp || '',
              phone: settings?.phone || '',
              email: settings?.email || ''
            });
          }
        } catch (error) {
          console.error('Error loading center info:', error);
        }
      } else {
        // No invitation code, load default settings
        try {
          const settings = await settingsAPI.getSiteSettings();
          setSiteSettings({
            siteName: settings?.site_name || settings?.siteName || 'TCF Canada',
            logo: settings?.logo || '',
            whatsapp: settings?.whatsapp || '',
            phone: settings?.phone || '',
            email: settings?.email || ''
          });
        } catch (error) {
          console.error('Error loading settings:', error);
        }
      }
    };
    
    loadSettings();
  }, [searchParams]);

  // Validate invitation code on change
  useEffect(() => {
    if (registerForm.invitationCode && registerForm.invitationCode.length >= 6) {
      usersAPI.getAdminByCode(registerForm.invitationCode.toUpperCase()).then(result => {
        if (result?.found) {
          setCenterInfo({
            name: result.admin.nom || result.admin.username,
            code: registerForm.invitationCode.toUpperCase()
          });
        } else {
          setCenterInfo(null);
        }
      }).catch(() => setCenterInfo(null));
    } else {
      setCenterInfo(null);
    }
  }, [registerForm.invitationCode]);

  useEffect(() => {
    if (user) {
      if (user.role === 'super_admin') {
        navigate('/super-admin');
      } else if (user.role === 'admin') {
        navigate('/admin-control');
      } else {
        navigate('/dashboard');
      }
    }
  }, [user, navigate]);

  const handleLogin = async (e) => {
    e.preventDefault();
    
    if (!username || !password) {
      toast.error('Veuillez remplir tous les champs');
      return;
    }

    const result = await login(username, password);
    if (result.success) {
      toast.success('Connexion réussie!');
    } else {
      // Check if it's a multiple device login error
      if (result.message && result.message.includes('plusieurs appareils')) {
        // Get admin's whatsapp from center settings
        let adminWhatsapp = siteSettings.whatsapp || '';
        setBlockedPopup({
          show: true,
          message: result.message,
          whatsapp: adminWhatsapp
        });
      } else {
        toast.error(result.message);
      }
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    
    // Validate all required fields
    if (!registerForm.nom.trim()) {
      toast.error('Le nom complet est requis');
      return;
    }
    if (!registerForm.email.trim()) {
      toast.error('L\'email est requis');
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(registerForm.email)) {
      toast.error('Email invalide');
      return;
    }
    if (!registerForm.telephone.trim()) {
      toast.error('Le numéro de téléphone est requis');
      return;
    }
    if (!registerForm.pays.trim()) {
      toast.error('Le pays est requis');
      return;
    }
    if (!registerForm.username.trim()) {
      toast.error('Le nom d\'utilisateur est requis');
      return;
    }
    if (registerForm.username.length < 3) {
      toast.error('Le nom d\'utilisateur doit contenir au moins 3 caractères');
      return;
    }
    if (!registerForm.password) {
      toast.error('Le mot de passe est requis');
      return;
    }
    if (registerForm.password.length < 6) {
      toast.error('Le mot de passe doit contenir au moins 6 caractères');
      return;
    }
    if (registerForm.password !== registerForm.confirmPassword) {
      toast.error('Les mots de passe ne correspondent pas');
      return;
    }

    const result = await register(
      registerForm.username, 
      registerForm.password,
      {
        nom: registerForm.nom,
        email: registerForm.email,
        telephone: registerForm.telephone,
        pays: registerForm.pays,
        invitationCode: registerForm.invitationCode || null
      }
    );
    
    if (result.success) {
      toast.success(result.message || 'Inscription réussie! Vous pouvez maintenant vous connecter.');
      setIsLogin(true);
      setCenterInfo(null);
      setRegisterForm({
        nom: '',
        email: '',
        telephone: '',
        pays: '',
        username: '',
        password: '',
        confirmPassword: '',
        invitationCode: ''
      });
    } else {
      toast.error(result.message);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    if (!forgotEmail.trim()) { toast.error('Veuillez entrer votre email'); return; }
    setForgotLoading(true);
    try {
      const result = await authAPI.forgotPassword(forgotEmail.trim());
      if (result.success) {
        toast.success(result.message);
        setResetEmail(forgotEmail.trim());
        setAuthView('reset');
      } else {
        toast.error(result.message || 'Erreur lors de l\'envoi');
      }
    } catch {
      toast.error('Erreur de connexion au serveur');
    } finally {
      setForgotLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    if (!resetCode.trim() || resetCode.length !== 6) { toast.error('Le code doit contenir 6 chiffres'); return; }
    if (!resetPassword || resetPassword.length < 6) { toast.error('Le mot de passe doit contenir au moins 6 caractères'); return; }
    if (resetPassword !== resetPasswordConfirm) { toast.error('Les mots de passe ne correspondent pas'); return; }
    setForgotLoading(true);
    try {
      const result = await authAPI.resetPassword(resetEmail, resetCode.trim(), resetPassword);
      if (result.success) {
        toast.success(result.message || 'Mot de passe réinitialisé !');
        setAuthView('login');
        setResetCode(''); setResetPassword(''); setResetPasswordConfirm(''); setForgotEmail('');
      } else {
        toast.error(result.message || 'Code invalide ou expiré');
      }
    } catch {
      toast.error('Erreur de connexion au serveur');
    } finally {
      setForgotLoading(false);
    }
  };

  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const handleGoogleSignIn = () => {
    const redirectUrl = window.location.origin + '/auth/callback';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const handleResetStorage = () => {
    if (window.confirm('Êtes-vous sûr de vouloir réinitialiser les données ? Cela recréera les comptes admin par défaut.')) {
      localStorage.removeItem('tcf-canada-data');
      localStorage.removeItem('tcf-current-user');
      window.location.reload();
    }
  };

  const countries = [
    'Canada', 'France', 'Belgique', 'Suisse', 'Maroc', 'Algérie', 'Tunisie', 
    'Sénégal', 'Côte d\'Ivoire', 'Cameroun', 'Haïti', 'Autre'
  ];

  return (
    <div className="min-h-screen bg-background flex relative">
      {/* Blocked Account Popup */}
      {blockedPopup.show && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm">
          <div className="bg-card border border-red-500/50 rounded-2xl shadow-2xl max-w-md w-full mx-4 overflow-hidden animate-in zoom-in-95">
            {/* Header */}
            <div className="bg-red-500/10 border-b border-red-500/20 p-6 text-center">
              <div className="h-16 w-16 mx-auto bg-red-500/20 rounded-full flex items-center justify-center mb-4">
                <AlertTriangle className="h-8 w-8 text-red-500" />
              </div>
              <h2 className="text-xl font-bold text-red-500">Compte Bloqué</h2>
            </div>
            
            {/* Content */}
            <div className="p-6 space-y-4">
              <p className="text-center text-muted-foreground">
                Votre compte a été archivé car vous avez tenté de vous connecter sur plusieurs appareils en même temps.
              </p>
              
              <div className="bg-secondary/50 rounded-xl p-4 text-center">
                <p className="text-sm font-medium mb-3">Pour réactiver votre compte, contactez votre administrateur :</p>
                
                {blockedPopup.whatsapp ? (
                  <a 
                    href={`https://wa.me/${blockedPopup.whatsapp.replace(/[^0-9]/g, '')}?text=Bonjour, mon compte a été bloqué sur la plateforme. Pouvez-vous le réactiver s'il vous plaît ?`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center space-x-2 bg-green-500 hover:bg-green-600 text-white px-6 py-3 rounded-xl font-medium transition-colors"
                  >
                    <MessageCircle className="h-5 w-5" />
                    <span>Contacter sur WhatsApp</span>
                  </a>
                ) : (
                  <div className="text-center">
                    <p className="text-sm text-muted-foreground mb-2">Contactez l'administrateur de votre centre par WhatsApp</p>
                    <div className="inline-flex items-center space-x-2 bg-green-500/20 text-green-600 px-4 py-2 rounded-xl">
                      <MessageCircle className="h-5 w-5" />
                      <span className="font-medium">WhatsApp</span>
                    </div>
                  </div>
                )}
              </div>
              
              <p className="text-xs text-center text-muted-foreground">
                L'administrateur pourra réactiver votre compte depuis son espace de gestion.
              </p>
            </div>
            
            {/* Footer */}
            <div className="border-t p-4">
              <button
                onClick={() => setBlockedPopup({ show: false, message: '', whatsapp: '' })}
                className="w-full py-2 text-sm text-muted-foreground hover:text-foreground transition-colors"
              >
                Fermer
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Left side - Form */}
      <div className="flex-1 flex items-center justify-center p-8 relative z-10">
        <div className="w-full max-w-md space-y-6 animate-in">
          <div className="fixed top-6 right-6">
            <DarkModeToggle />
          </div>
          
          <div className="text-center">
            {siteSettings.logo ? (
              <img 
                src={siteSettings.logo.startsWith('http') ? siteSettings.logo : `${process.env.REACT_APP_BACKEND_URL || ''}${siteSettings.logo}`} 
                alt={siteSettings.siteName} 
                className="h-16 mx-auto mb-4 object-contain"
              />
            ) : null}
            <h1 className="text-2xl sm:text-4xl md:text-5xl font-bold text-primary mb-2">{siteSettings.siteName}</h1>
            <p className="text-base text-muted-foreground">Plateforme de préparation professionnelle</p>
          </div>

          {authView === 'forgot' ? (
            /* ── Forgot Password ── */
            <div className="bg-card p-6 rounded-xl border shadow-sm space-y-5">
              <div className="flex items-center space-x-3">
                <button
                  type="button"
                  onClick={() => setAuthView('login')}
                  className="text-muted-foreground hover:text-foreground transition-colors"
                >
                  <ArrowLeft className="h-5 w-5" />
                </button>
                <div>
                  <h2 className="text-2xl font-semibold">Mot de passe oublié</h2>
                  <p className="text-sm text-muted-foreground">Entrez votre email pour recevoir un code</p>
                </div>
              </div>
              <form onSubmit={handleForgotPassword} className="space-y-4">
                <div className="space-y-2">
                  <label className="text-sm font-medium flex items-center space-x-2">
                    <Mail className="h-4 w-4" />
                    <span>Adresse email</span>
                  </label>
                  <input
                    type="email"
                    value={forgotEmail}
                    onChange={(e) => setForgotEmail(e.target.value)}
                    className="flex h-10 w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
                    placeholder="votre@email.com"
                    autoFocus
                  />
                </div>
                <button
                  type="submit"
                  disabled={forgotLoading}
                  className="w-full h-10 px-4 py-2 rounded-lg font-medium bg-primary text-primary-foreground hover:bg-primary/90 shadow-sm transition-colors disabled:opacity-60 flex items-center justify-center space-x-2"
                >
                  {forgotLoading
                    ? <Loader2 className="h-4 w-4 animate-spin" />
                    : <KeyRound className="h-4 w-4" />}
                  <span>{forgotLoading ? 'Envoi en cours...' : 'Envoyer le code'}</span>
                </button>
              </form>
            </div>
          ) : authView === 'reset' ? (
            /* ── Reset Password ── */
            <div className="bg-card p-6 rounded-xl border shadow-sm space-y-5">
              <div className="flex items-center space-x-3">
                <button
                  type="button"
                  onClick={() => setAuthView('forgot')}
                  className="text-muted-foreground hover:text-foreground transition-colors"
                >
                  <ArrowLeft className="h-5 w-5" />
                </button>
                <div>
                  <h2 className="text-2xl font-semibold">Nouveau mot de passe</h2>
                  <p className="text-sm text-muted-foreground">Code envoyé à <strong>{resetEmail}</strong></p>
                </div>
              </div>
              <form onSubmit={handleResetPassword} className="space-y-4">
                <div className="space-y-2">
                  <label className="text-sm font-medium">Code de vérification (6 chiffres)</label>
                  <input
                    type="text"
                    inputMode="numeric"
                    value={resetCode}
                    onChange={(e) => setResetCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    className="flex h-12 w-full rounded-md border border-input bg-transparent px-3 py-2 text-center tracking-[0.6em] font-mono text-xl ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
                    placeholder="000000"
                    maxLength={6}
                    autoFocus
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium flex items-center space-x-2">
                    <Lock className="h-4 w-4" />
                    <span>Nouveau mot de passe</span>
                  </label>
                  <div className="relative">
                    <input
                      type={showResetPassword ? 'text' : 'password'}
                      value={resetPassword}
                      onChange={(e) => setResetPassword(e.target.value)}
                      className="flex h-10 w-full rounded-md border border-input bg-transparent px-3 py-2 pr-10 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
                      placeholder="Minimum 6 caractères"
                    />
                    <button
                      type="button"
                      onClick={() => setShowResetPassword(!showResetPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                    >
                      {showResetPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium flex items-center space-x-2">
                    <Lock className="h-4 w-4" />
                    <span>Confirmer le mot de passe</span>
                  </label>
                  <input
                    type="password"
                    value={resetPasswordConfirm}
                    onChange={(e) => setResetPasswordConfirm(e.target.value)}
                    className="flex h-10 w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
                    placeholder="Confirmez le mot de passe"
                  />
                </div>
                <button
                  type="submit"
                  disabled={forgotLoading}
                  className="w-full h-10 px-4 py-2 rounded-lg font-medium bg-primary text-primary-foreground hover:bg-primary/90 shadow-sm transition-colors disabled:opacity-60 flex items-center justify-center space-x-2"
                >
                  {forgotLoading && <Loader2 className="h-4 w-4 animate-spin" />}
                  <span>{forgotLoading ? 'Réinitialisation...' : 'Réinitialiser le mot de passe'}</span>
                </button>
              </form>
              <div className="text-center">
                <button
                  type="button"
                  onClick={() => { setAuthView('forgot'); setForgotEmail(resetEmail); }}
                  className="text-sm text-primary hover:underline"
                >
                  Renvoyer le code
                </button>
              </div>
            </div>
          ) : (
            /* ── Login / Register ── */
          <div className="bg-card p-6 rounded-xl border shadow-sm space-y-5">
            <div className="space-y-2">
              <h2 className="text-2xl font-semibold">{isLogin ? 'Connexion' : 'Inscription'}</h2>
              <p className="text-sm text-muted-foreground">
                {isLogin ? 'Connectez-vous pour accéder à vos modules' : 'Créez votre compte pour commencer'}
              </p>
            </div>

            {/* Google Sign In Button */}
            <button
              onClick={handleGoogleSignIn}
              type="button"
              className="w-full h-11 px-4 py-2 rounded-lg font-medium border-2 border-border bg-background hover:bg-secondary flex items-center justify-center space-x-3 transition-colors"
              data-testid="google-signin-button"
            >
              <svg className="h-5 w-5" viewBox="0 0 24 24">
                <path
                  fill="#4285F4"
                  d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                />
                <path
                  fill="#34A853"
                  d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                />
                <path
                  fill="#FBBC05"
                  d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                />
                <path
                  fill="#EA4335"
                  d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                />
              </svg>
              <span>{isLogin ? 'Continuer avec Google' : 'S\'inscrire avec Google'}</span>
            </button>

            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <span className="w-full border-t" />
              </div>
              <div className="relative flex justify-center text-xs uppercase">
                <span className="bg-card px-2 text-muted-foreground">ou</span>
              </div>
            </div>

            {isLogin ? (
              /* Login Form */
              <form onSubmit={handleLogin} className="space-y-4" data-testid="login-form">
                <div className="space-y-2">
                  <label htmlFor="username" className="text-sm font-medium flex items-center space-x-2">
                    <User className="h-4 w-4" />
                    <span>Nom d'utilisateur ou Email</span>
                  </label>
                  <input
                    id="username"
                    type="text"
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    data-testid="username-input"
                    className="flex h-10 w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
                    placeholder="Utilisateur ou email"
                  />
                </div>

                <div className="space-y-2">
                  <label htmlFor="password" className="text-sm font-medium flex items-center space-x-2">
                    <Lock className="h-4 w-4" />
                    <span>Mot de passe</span>
                  </label>
                  <div className="relative">
                    <input
                      id="password"
                      type={showPassword ? 'text' : 'password'}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      data-testid="password-input"
                      className="flex h-10 w-full rounded-md border border-input bg-transparent px-3 py-2 pr-10 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
                      placeholder="Entrez votre mot de passe"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                    >
                      {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                  <div className="text-right">
                    <button
                      type="button"
                      onClick={() => { setAuthView('forgot'); setForgotEmail(''); }}
                      className="text-sm text-primary hover:underline"
                    >
                      Mot de passe oublié ?
                    </button>
                  </div>
                </div>

                <button
                  type="submit"
                  data-testid="submit-button"
                  className="w-full h-10 px-4 py-2 rounded-lg font-medium bg-primary text-primary-foreground hover:bg-primary/90 shadow-sm transition-colors focus:ring-2 focus:ring-primary focus:ring-offset-2"
                >
                  Se connecter
                </button>
              </form>
            ) : (
              /* Registration Form */
              <form onSubmit={handleRegister} className="space-y-4" data-testid="register-form">
                {/* Invitation Code (Optional) */}
                <div className="space-y-2">
                  <label className="text-sm font-medium flex items-center space-x-2">
                    <Ticket className="h-4 w-4" />
                    <span>Code d'invitation (optionnel)</span>
                  </label>
                  <input
                    type="text"
                    value={registerForm.invitationCode}
                    onChange={(e) => setRegisterForm(prev => ({ ...prev, invitationCode: e.target.value.toUpperCase() }))}
                    data-testid="register-invitation-code-input"
                    className={`flex h-10 w-full rounded-md border px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 uppercase tracking-wider font-mono ${
                      centerInfo ? 'border-green-500 bg-green-50 dark:bg-green-900/20' : 'border-input bg-transparent'
                    }`}
                    placeholder="EX: ABCD1234"
                    maxLength={8}
                  />
                  {centerInfo && (
                    <p className="text-xs text-green-600 dark:text-green-400 flex items-center space-x-1">
                      <span>✓</span>
                      <span>Centre: {centerInfo.name}</span>
                    </p>
                  )}
                  {registerForm.invitationCode && registerForm.invitationCode.length >= 6 && !centerInfo && (
                    <p className="text-xs text-red-500">Code invalide</p>
                  )}
                </div>

                {/* Full Name */}
                <div className="space-y-2">
                  <label className="text-sm font-medium flex items-center space-x-2">
                    <User className="h-4 w-4" />
                    <span>Nom complet *</span>
                  </label>
                  <input
                    type="text"
                    value={registerForm.nom}
                    onChange={(e) => setRegisterForm(prev => ({ ...prev, nom: e.target.value }))}
                    data-testid="register-nom-input"
                    className="flex h-10 w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
                    placeholder="Jean Dupont"
                  />
                </div>

                {/* Email */}
                <div className="space-y-2">
                  <label className="text-sm font-medium flex items-center space-x-2">
                    <Mail className="h-4 w-4" />
                    <span>Email *</span>
                  </label>
                  <input
                    type="email"
                    value={registerForm.email}
                    onChange={(e) => setRegisterForm(prev => ({ ...prev, email: e.target.value }))}
                    data-testid="register-email-input"
                    className="flex h-10 w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
                    placeholder="votre@email.com"
                  />
                </div>

                {/* Phone and Country in grid */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div className="space-y-2">
                    <label className="text-sm font-medium flex items-center space-x-2">
                      <Phone className="h-4 w-4" />
                      <span>Téléphone *</span>
                    </label>
                    <input
                      type="tel"
                      value={registerForm.telephone}
                      onChange={(e) => setRegisterForm(prev => ({ ...prev, telephone: e.target.value }))}
                      data-testid="register-telephone-input"
                      className="flex h-10 w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
                      placeholder="+1 234 567 890"
                    />
                  </div>

                  <div className="space-y-2">
                    <label className="text-sm font-medium flex items-center space-x-2">
                      <MapPin className="h-4 w-4" />
                      <span>Pays *</span>
                    </label>
                    <select
                      value={registerForm.pays}
                      onChange={(e) => setRegisterForm(prev => ({ ...prev, pays: e.target.value }))}
                      data-testid="register-pays-input"
                      className="flex h-10 w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
                    >
                      <option value="">Sélectionner...</option>
                      {countries.map(country => (
                        <option key={country} value={country}>{country}</option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* Username */}
                <div className="space-y-2">
                  <label className="text-sm font-medium flex items-center space-x-2">
                    <User className="h-4 w-4" />
                    <span>Nom d'utilisateur *</span>
                  </label>
                  <input
                    type="text"
                    value={registerForm.username}
                    onChange={(e) => setRegisterForm(prev => ({ ...prev, username: e.target.value }))}
                    data-testid="register-username-input"
                    className="flex h-10 w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
                    placeholder="Choisissez un nom d'utilisateur"
                  />
                </div>

                {/* Password */}
                <div className="space-y-2">
                  <label className="text-sm font-medium flex items-center space-x-2">
                    <Lock className="h-4 w-4" />
                    <span>Mot de passe *</span>
                  </label>
                  <div className="relative">
                    <input
                      type={showPassword ? 'text' : 'password'}
                      value={registerForm.password}
                      onChange={(e) => setRegisterForm(prev => ({ ...prev, password: e.target.value }))}
                      data-testid="register-password-input"
                      className="flex h-10 w-full rounded-md border border-input bg-transparent px-3 py-2 pr-10 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
                      placeholder="Minimum 6 caractères"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                    >
                      {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                </div>

                {/* Confirm Password */}
                <div className="space-y-2">
                  <label className="text-sm font-medium flex items-center space-x-2">
                    <Lock className="h-4 w-4" />
                    <span>Confirmer le mot de passe *</span>
                  </label>
                  <input
                    type="password"
                    value={registerForm.confirmPassword}
                    onChange={(e) => setRegisterForm(prev => ({ ...prev, confirmPassword: e.target.value }))}
                    data-testid="register-confirm-password-input"
                    className="flex h-10 w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
                    placeholder="Confirmez votre mot de passe"
                  />
                </div>

                <button
                  type="submit"
                  data-testid="submit-button"
                  className="w-full h-10 px-4 py-2 rounded-lg font-medium bg-primary text-primary-foreground hover:bg-primary/90 shadow-sm transition-colors focus:ring-2 focus:ring-primary focus:ring-offset-2"
                >
                  S'inscrire
                </button>
              </form>
            )}

            <div className="text-center">
              <button
                onClick={() => setIsLogin(!isLogin)}
                data-testid="toggle-auth-mode"
                className="text-sm text-primary hover:underline"
              >
                {isLogin ? "Pas encore de compte ? S'inscrire" : 'Déjà un compte ? Se connecter'}
              </button>
            </div>

          </div>
          )}
        </div>
      </div>

      {/* Right side - Image */}
      <div className="hidden lg:block flex-1 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-primary/10 via-transparent to-accent/10" />
        <img
          src="https://images.unsplash.com/photo-1767102060241-130cb9260718?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzN8MHwxfHNlYXJjaHwxfHxzdHVkZW50JTIwc3R1ZHlpbmclMjBtb2Rlcm4lMjBsaWJyYXJ5JTIwbGFwdG9wfGVufDB8fHx8MTc3MzQwMDIzOXww&ixlib=rb-4.1.0&q=85"
          alt="Student studying"
          className="absolute inset-0 w-full h-full object-cover opacity-80"
        />
      </div>
    </div>
  );
}
